import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  TextField,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PriceDisplay from "components/PriceDisplay";
import iaasService from "services/iaasService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function PBXCloudConfig({ onBack }) {
  const { user } = useContext(AuthContext);
  const [concurrentCalls, setConcurrentCalls] = useState(4); // Erlaubte Werte: 4, 8, 16, 24
  const [version, setVersion] = useState('pro'); // 'pro' oder 'enterprise'
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);

  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  // Berechne den Preis mit Berücksichtigung des Partner-Status
  useEffect(() => {
    const config = {
      concurrentCalls,
      version
    };
    
    // Berechne den Basispreis
    const basePrice = iaasService.calculatePBXPrice(config);
    
    const isPartner = user?.isPartner === true;

    if (isPartner) {
      // 20% Rabatt für Partner
      setOriginalPrice(basePrice);
      setTotalPrice(basePrice * 0.8);
      setDiscountApplied(true);
    } else {
      setTotalPrice(basePrice);
      setOriginalPrice(null);
      setDiscountApplied(false);
    }
  }, [concurrentCalls, version, user?.isPartner]);

  const handleOrder = async () => {
    if (!agreedToTerms) {
      showErrorNotification("Bitte stimmen Sie den AGBs zu.");
      return;
    }

    try {
      const configuration = {
        concurrentCalls,
        version
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        configuration.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      const response = await iaasService.orderService(
        'pbx',
        configuration
      );

      if (response) {
        showSuccessNotification("Ihre Bestellung wurde erfolgreich aufgegeben!");
        setTimeout(() => {
          window.location.href = "/iaas/server";
        }, 2000);
      }
    } catch (error) {
      console.error("Fehler bei der Bestellung:", error);
      showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
    }
  };

  return (
    <SoftBox>
      <SoftTypography variant="h4" mb={2}>
        UDS PBX Cloud (3CX) Konfiguration
      </SoftTypography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} mx="auto">
          <TextField
            fullWidth
            select
            label="Version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            helperText="Wählen Sie zwischen PRO und Enterprise Version"
            SelectProps={{
              native: true,
            }}
            sx={{ mb: 2 }}
          >
            <option value="pro">PRO Version</option>
            <option value="enterprise">Enterprise Version</option>
          </TextField>
          
          <TextField
            fullWidth
            select
            label="Gleichzeitige Gespräche"
            value={concurrentCalls}
            onChange={(e) => setConcurrentCalls(parseInt(e.target.value))}
            helperText="Wählen Sie die Anzahl gleichzeitiger Gespräche (10€ pro 5 Gespräche/Monat)"
            SelectProps={{
              native: true,
            }}
          >
            <option value={4}>4</option>
            <option value={8}>8</option>
            <option value={16}>16</option>
            <option value={24}>24</option>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <SoftTypography variant="h5" fontWeight="bold">
            <PriceDisplay 
              price={totalPrice} 
              originalPrice={originalPrice} 
              discountApplied={discountApplied} 
              title="Monatlicher Preis:"
            />
          </SoftTypography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
            }
            label={
              <SoftTypography variant="body2">
                Ich akzeptiere die{" "}
                <a href="/agb" target="_blank" rel="noopener noreferrer">
                  AGBs
                </a>{" "}
                und bestelle die PBX Cloud kostenpflichtig.
              </SoftTypography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SoftBox display="flex" justifyContent="space-between">
            <Button variant="contained" color="light" onClick={onBack}>
              Zurück
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOrder}
              disabled={!agreedToTerms}
            >
              Jetzt bestellen
            </Button>
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

PBXCloudConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default PBXCloudConfig;